'use client'
import { useParams } from 'next/navigation'
import type { TypeLang } from '@/schema'
import styled from 'styled-components'
import Link from 'next/link'
import { designGrid, mq, presetsFluid, vw } from '@/styles'

type TypeNextErrorProps = {
  error: Error & { digest?: string }
  reset: () => void
  params: Promise<{
    lang: TypeLang
  }>
}


// Root keys must be TypeLang codes
const ERROR_PAGE_TRANSLATIONS: Record<TypeLang, { [key: string]: string }> = {
  es: {
    title: 'Páginas del catálogo no disponibles temporalmente',
    description: 'Estamos realizando mejoras en la página del catálogo, estaremos de vuelta lo antes posible.',
    buttonText: 'Ir al inicio'
  },
  en: {
    title: 'Catalog pages temporarily unavailable',
    description: 'We are making improvements to the catalog page, we will be back as soon as possible.',
    buttonText: 'Go to home'
  }
}

export default function Error({}:TypeNextErrorProps) {
  const { lang } = useParams() as { lang: TypeLang }

  return (
    <Header>
      <hgroup>
        <h1>{ERROR_PAGE_TRANSLATIONS[lang].title}</h1>
        <p>{ERROR_PAGE_TRANSLATIONS[lang].description}</p>
        <LinkCta href={`/${lang}`}>{ERROR_PAGE_TRANSLATIONS[lang].buttonText}</LinkCta>
      </hgroup>
    </Header>
  )
}

const Header = styled.header`
  ${designGrid({})}
  grid-column: 1 / span 6;
  margin-bottom: 100px;
  margin-top: 70px;
  overflow: hidden;
  padding: 30px 0;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }

  ${mq.greaterThan('desktop-xs')} {
    margin-top: 90px;
  }

  hgroup {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 3 / span 8;
    }
  }

  h1,
  p {
    text-align: left;
  }

  h1 {
    ${presetsFluid.font32_40()}
    margin-bottom: 20px;

		${mq.greaterThan('desktop')} {
			${presetsFluid.font40_rel()}
		}
  }

  p {
    ${presetsFluid.font25_30()}
    margin-bottom: 40px;

		${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
		}
  }
`


const LinkCta = styled(Link)`
  ${presetsFluid.font15()}
  align-items: flex-end;
  background-color: var(--color-background--dark);
  color: var(--color-text--dark);
  display: flex;
  height: ${vw(60, 'mobile')};
  grid-column: 1 / span 6;
  justify-content: flex-start;
  min-height: 50px;
  min-width: 215px;
  padding: ${vw(10, 'mobile')} ${vw(30, 'mobile')} ${vw(10, 'mobile')} ${vw(10, 'mobile')};
  transition: 300ms background-color linear;
  width: auto;
  width: fit-content;

  ${mq.greaterThan('nexus7')} {
    height: ${vw(60, 'tablet')};
    padding: ${vw(10, 'tablet')} ${vw(30, 'tablet')} ${vw(10, 'tablet')} ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 4 / span 3;
    height: ${vw(60, 'desktop')};
    margin-bottom: 0;
    min-width: 160px;
    padding: ${vw(10, 'desktop')} ${vw(30, 'desktop')} ${vw(10, 'desktop')} ${vw(10, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
		${presetsFluid.font15_rel()}
    height: 60px;
    min-width: 180px;
    padding: 10px 30px 10px 10px;
  }

  &:hover {
    background-color: var(--color-blue);
  }
`
